import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import {
    Anchor,
    Body1,
    Flex,
    Heading1,
    PinInput,
    Stack,
    useNavBar,
} from '@myphx-engineering/phx-design-system';
import { useHotkeys } from '@myphx-engineering/phx-design-system/hooks';
import { PhoneNumberFormatter } from '@phx/utils';

import { checkAccountLockoutApi } from '../../api/check-account-lockout.api';
import { sendVerificationCodeApi } from '../../api/send-verification-code.api';
import { verifyCodeApi } from '../../api/verify-code.api';
import { InfoMap } from '../../components/alerts/information/information-map';
import { PageLayout } from '../../components/layout/PageLayout';
import { telemetryInstance } from '../../instrumentation';
import { routes } from '../../routes';
import { getErrorMessage } from '../../utils/get-error-message';

import type { MfaSelectPageState } from './MfaSelectPage';

const TotpPreSetupPage = () => {
    const { t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();
    const { setBackOverrideState } = useNavBar();

    const state = location?.state as MfaSelectPageState;

    const [loading, setLoading] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string | undefined>();
    const [showResendMessage, setShowResendMessage] = useState<boolean>(false);
    const [code, setCode] = useState<string>('');

    const ResendMessage = showResendMessage ? InfoMap['CodeResentInfo'] : null;

    const isFormValid = code.length === 6;

    const onNavigateBack = useCallback(() => {
        navigate(-1);
    }, [navigate]);

    useEffect(() => {
        setBackOverrideState({
            overrideFn: onNavigateBack,
        });

        return () => setBackOverrideState({ overrideFn: null });
    }, [onNavigateBack, setBackOverrideState]);

    const onVerifyCodeClick = async (verificationCode: string) => {
        telemetryInstance.logEvent({
            name: 'OTPVerifySubmitted',
            source: location.pathname,
        });
        setErrorMessage(undefined);
        setShowResendMessage(false);
        setLoading(true);

        const verifyResult = await verifyCodeApi(
            state.phoneNumber,
            verificationCode
        );

        if (!verifyResult.success) {
            telemetryInstance.logError({
                source: location.pathname,
                exception: new Error(
                    `Code verification failed: ${verifyResult.error}`
                ),
            });

            // If user is locked out (429), redirect back to sign-in with lockout flag and phone number
            if (verifyResult.error === '429_MaxAttemptReached') {
                navigate(
                    { pathname: routes.signIn, search: window.location.search },
                    {
                        state: {
                            lockoutError: true,
                            phoneNumber: state.phoneNumber,
                        },
                    }
                );
                return;
            }

            // If invalid code, check remaining attempts
            if (verifyResult.error === '400_InvalidCode') {
                const lockoutStatus = await checkAccountLockoutApi(
                    state.phoneNumber
                );
                setLoading(false);
                setErrorMessage(
                    getErrorMessage(
                        t,
                        verifyResult.error,
                        lockoutStatus.remainingAttempts
                    )
                );
                setCode('');
                return;
            }

            setLoading(false);
            setErrorMessage(getErrorMessage(t, verifyResult.error));
            setCode('');
            return;
        }

        telemetryInstance.logEvent({
            name: 'PhoneVerified',
            source: location.pathname,
        });

        navigate(
            {
                pathname: routes.totpSetup,
                search: window.location.search,
            },
            {
                state: {
                    phoneNumber: state.phoneNumber,
                    password: state.password,
                    callbackUrl: state.callbackUrl,
                    kratosIdentityId: state.kratosIdentityId,
                    kratosSessionToken: state.kratosSessionToken,
                },
            }
        );
    };

    const onSendCodeClick = async () => {
        setLoading(true);

        const result = await sendVerificationCodeApi(state.phoneNumber);

        if (result.success) {
            setErrorMessage(undefined);
            setShowResendMessage(true);
            telemetryInstance.logEvent({
                name: 'OTPResent',
                source: location.pathname,
            });
        } else {
            setErrorMessage(getErrorMessage(t, result.error));
            setShowResendMessage(false);
            telemetryInstance.logError({
                source: location.pathname,
                exception: new Error(result.error || 'OTP resend failed'),
            });
        }

        setLoading(false);
    };

    useHotkeys([
        [
            'Enter',
            () => {
                if (isFormValid) {
                    onVerifyCodeClick(code);
                }
            },
        ],
    ]);

    return (
        <PageLayout
            loadingOverlay={loading}
            header={
                <>{showResendMessage && ResendMessage && <ResendMessage />}</>
            }
            heading={
                <Stack gap="sm">
                    <Heading1>{t('totpPreSetupPage.title')}</Heading1>
                    <Body1>
                        {t('totpPreSetupPage.desc', {
                            phoneNumber: PhoneNumberFormatter.formatForUI(
                                state.phoneNumber
                            ),
                        })}
                    </Body1>
                </Stack>
            }
        >
            <Stack gap="md">
                <PinInput
                    length={6}
                    size="xl"
                    gap="md"
                    // oxlint-disable-next-line jsx-a11y/no-autofocus -- tracked in docs/oxlint-violations.md
                    autoFocus
                    oneTimeCode
                    placeholder=""
                    value={code}
                    onChange={setCode}
                    onComplete={onVerifyCodeClick}
                    error={errorMessage}
                />
            </Stack>
            <Flex justify="left">
                <Body1>
                    {t('verifyAccountPage.receiveCode')}
                    <Anchor onClick={onSendCodeClick}>
                        {t('verifyAccountPage.resend')}
                    </Anchor>
                </Body1>
            </Flex>
        </PageLayout>
    );
};
export default TotpPreSetupPage;
